import React, { createContext, useContext, useMemo } from 'react';
import { useEmailSignatureSelection } from '@/components/Drawers/AddUnlayerEmailDrawer/hooks/useEmailSignatureSelection';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useSelector } from 'react-redux';

const UnlayerSignatureContext = createContext(null);

/**
 * Owns compose signature selection state and delegated from-address options.
 */
export const UnlayerSignatureProvider = ({ children }) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const delegatedEmails = useMemo(
		() => userDetails?.delegatedEmails ?? [],
		[userDetails?.delegatedEmails],
	);

	const signatureSelection = useEmailSignatureSelection();

	const value = useMemo(
		() => ({
			delegatedEmails,
			handleSignatureSelect: signatureSelection.handleSignatureSelect,
			isFetchingSignature: signatureSelection.isFetchingSignature,
			isSignatureDisabled: signatureSelection.isSignatureDisabled,
			selectedSignature: signatureSelection.selectedSignature,
			signatureDisabledReason: signatureSelection.signatureDisabledReason,
			signatureError: signatureSelection.signatureError,
			signatureInsertRequest: signatureSelection.signatureInsertRequest,
			signatureOptions: signatureSelection.signatureOptions,
		}),
		[
			delegatedEmails,
			signatureSelection.handleSignatureSelect,
			signatureSelection.isFetchingSignature,
			signatureSelection.isSignatureDisabled,
			signatureSelection.selectedSignature,
			signatureSelection.signatureDisabledReason,
			signatureSelection.signatureError,
			signatureSelection.signatureInsertRequest,
			signatureSelection.signatureOptions,
		],
	);

	return (
		<UnlayerSignatureContext.Provider value={value}>{children}</UnlayerSignatureContext.Provider>
	);
};

export const useUnlayerSignatureContext = () => {
	const context = useContext(UnlayerSignatureContext);
	if (!context) {
		throw new Error('useUnlayerSignatureContext must be used within an UnlayerSignatureProvider');
	}
	return context;
};
